<template>
  <div class="m-checkbox">
    <div v-if="isRadioGroup" class="m-checkbox-group m-checkbox-group-radio">
      <label v-for="option in normalizedOptions" :key="option.id" class="m-checkbox-option m-checkbox-option-radio">
        <input
          v-model="selectedOptionId"
          class="m-checkbox-radio-input"
          type="radio"
          :name="inputId"
          :value="option.id"
          :disabled="disabled || readonly"
          :checked="option.value"
          @change="emitChange"
          @blur="handleBlur"
        >
        <span class="m-checkbox-label">{{ option.label }}</span>
      </label>
    </div>

    <label v-else class="m-checkbox-single">
      <Checkbox
        v-model="internalValue"
        :binary="true"
        :inputId="inputId"
        :disabled="disabled || readonly"
        @change="emitChange"
        @blur="handleBlur"
      />
      <span class="m-checkbox-label">{{ singleLabel }}</span>
    </label>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import Checkbox from 'primevue/checkbox'
import {
  CHECKBOX_USER_EMIT_GUARD_MS,
  coerceCheckboxBinaryModelValue,
  getCheckboxRowId,
  isSelectedCheckboxRow,
  shouldApplyIncomingCheckboxModelValue,
} from '@/common/utils/checkboxModelSync'

interface Option {
  id: any
  value?: any
  label: string
}

interface Props {
  modelValue?: boolean | any[] | Record<string, any> | null
  options?: Option[]
  label?: string
  disabled?: boolean
  readonly?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  options: () => [],
  label: '',
  disabled: false,
  readonly: false,
})

const emit = defineEmits<{
  'update:modelValue': (value: boolean | Record<string, any>) => void
  change: (value: boolean | Record<string, any>) => void
  blur: () => void
}>()

const inputId = `checkbox-${Math.random().toString(36).substr(2, 9)}`
const normalizedOptions = computed(() =>
  (props.options || [])
    .map((option) => ({
      id: option.id ?? option.value ?? '',
      value: option.value === 'true' || option.value === true,
      label: option.label ?? String(option.id ?? option.value ?? ''),
    }))
    .filter((option) => option.id !== '' && option.label !== ''),
)
const isRadioGroup = computed(() => normalizedOptions.value.length > 1)
const singleLabel = computed(() => normalizedOptions.value[0]?.label || props.label)
const internalValue = ref<boolean>(false)
const selectedOptionId = ref<any>(null)
const lastUserEmitted = ref<boolean | Record<string, any> | null>(null)
const lastUserEmittedAt = ref<number | null>(null)
const lastReinforceAt = ref(0)

const applyIncomingModelValue = (newValue: unknown) => {
  if (isRadioGroup.value) {
    if (newValue && typeof newValue === 'object' && Array.isArray((newValue as any).rows)) {
      const selectedRow = (newValue as any).rows.find((row: any) => isSelectedCheckboxRow(row))
      selectedOptionId.value = selectedRow ? getCheckboxRowId(selectedRow) : null
    } else if (Array.isArray(newValue)) {
      const selectedRow = newValue.find((row: any) => isSelectedCheckboxRow(row))
      selectedOptionId.value = selectedRow ? getCheckboxRowId(selectedRow) : newValue[0] ?? null
    } else {
      selectedOptionId.value = newValue ?? null
    }
  } else {
    if (newValue && typeof newValue === 'object' && Array.isArray((newValue as any).rows)) {
      const firstRow = (newValue as any).rows[0]
      internalValue.value = isSelectedCheckboxRow(firstRow)
    } else if (Array.isArray(newValue)) {
      internalValue.value = newValue.some((row: any) => isSelectedCheckboxRow(row))
    } else {
      internalValue.value = coerceCheckboxBinaryModelValue(newValue)
    }
  }
}

watch(
  () => props.modelValue,
  (newValue) => {
    if (
      !shouldApplyIncomingCheckboxModelValue({
        incoming: newValue,
        lastUserEmitted: lastUserEmitted.value,
        lastUserEmittedAt: lastUserEmittedAt.value,
      })
    ) {
      // Keep local UI; re-assert user value so concurrent table onChange/refill cannot stick stale false.
      const now = Date.now()
      if (
        lastUserEmitted.value !== null &&
        lastUserEmitted.value !== undefined &&
        now - lastReinforceAt.value > 50
      ) {
        lastReinforceAt.value = now
        emit('update:modelValue', lastUserEmitted.value)
      }
      return
    }
    applyIncomingModelValue(newValue)
    // Keep guard for the full window so a later stale false (EdiTable onChange) cannot win
    // after an intermediate parent echo of the user value.
    if (
      lastUserEmittedAt.value != null &&
      Date.now() - lastUserEmittedAt.value > CHECKBOX_USER_EMIT_GUARD_MS
    ) {
      lastUserEmitted.value = null
      lastUserEmittedAt.value = null
    }
  },
  { immediate: true, deep: true },
)

const buildTableValue = (): Record<string, any> => {
  const optionCount = normalizedOptions.value.length
  return {
    columns: ['id', 'description', 'value'],
    rows: normalizedOptions.value.map((option) => ({
      id: option.id,
      description: option.label,
      value: optionCount > 1 ? selectedOptionId.value === option.id : internalValue.value,
    })),
    empty: normalizedOptions.value.length === 0,
  }
}

const emitChange = () => {
  const valueToEmit = normalizedOptions.value.length > 0 ? buildTableValue() : internalValue.value
  lastUserEmitted.value = valueToEmit
  lastUserEmittedAt.value = Date.now()
  emit('update:modelValue', valueToEmit)
  emit('change', valueToEmit)
}

const handleBlur = () => {
  emit('blur')
}

defineExpose({
  getLiveTableValue: () =>
    normalizedOptions.value.length > 0 ? buildTableValue() : internalValue.value,
})
</script>

<style scoped lang="scss">
.m-checkbox {
  display: flex;
  flex-direction: column;
  --p-checkbox-background: #ffffff;
  --p-checkbox-disabled-background: #f3f4f6;
  --p-checkbox-icon-checked-color: #ffffff;
  --p-checkbox-icon-checked-hover-color: #ffffff;
  --p-checkbox-checked-background: var(--primary-color, #1e3a6e);
  --p-checkbox-checked-border-color: var(--primary-color, #1e3a6e);
  --p-checkbox-checked-hover-background: var(--primary-color, #1e3a6e);
  --p-checkbox-checked-hover-border-color: var(--primary-color, #1e3a6e);
}

.m-checkbox-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.m-checkbox-group-radio {
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--p-content-border-color, #d1d5db);
  border-radius: 6px;
  background: var(--p-content-background, #fff);
}

.m-checkbox-option,
.m-checkbox-single {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.25rem 0;
}

.m-checkbox-label {
  font-size: 14px;
  color: currentColor;
  user-select: none;
  margin: 0;
}

.m-checkbox-radio-input {
  margin: 0;
  accent-color: var(--p-primary-color, var(--primary-color, #1e3a6e));
}

:deep(.p-checkbox) {
  flex-direction: row;
  align-items: center;
  position: relative;

  .p-checkbox-input {
    appearance: none;
    opacity: 0;
    position: absolute;
    inset: 0;
  }

  .p-checkbox-box {
    border: 1.5px solid #9ca3af;
    border-radius: 6px;
    width: 1.125rem;
    height: 1.125rem;
    background: #ffffff;
    transition:
      background-color 0.12s ease,
      border-color 0.12s ease,
      box-shadow 0.12s ease;

    &.p-focus,
    &:focus-visible {
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #1e3a6e) 18%, transparent);
    }

    .pi,
    .p-checkbox-icon,
    svg {
      font-size: 0.7rem;
      color: #ffffff !important;
      fill: #ffffff !important;
    }
  }

  &[data-p-checked='true'] .p-checkbox-box,
  .p-checkbox-box.p-highlight {
    background: var(--primary-color, #1e3a6e) !important;
    border-color: var(--primary-color, #1e3a6e) !important;
  }

  &:not(.p-disabled):not([data-p-checked='true']) .p-checkbox-box:hover {
    border-color: var(--primary-color, #1e3a6e);
  }
}
</style>
